/**
 * Native (FCM) push notifications for the Capacitor app.
 *
 * No-ops on the web (web push is handled separately via the service worker and
 * utils/pushNotifications.js). On Android/iOS: asks permission, registers with
 * FCM, sends the device token to the backend (/api/fcm/register), and opens the
 * right screen when a push is tapped.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import Cookies from 'js-cookie';
import axiosinstance from '../axios/axiosInstance';

let cachedToken = null;
let listenersAdded = false;

async function postToken() {
  const userId = Cookies.get('user_id');
  if (!cachedToken || !userId) return;
  try {
    await axiosinstance.post('/fcm/register', {
      user_id: userId,
      token: cachedToken,
      platform: Capacitor.getPlatform(),
    });
    console.log('[push] FCM token registered for user', userId);
  } catch (e) {
    console.error('[push] Failed to register FCM token:', e?.message || e);
  }
}

/**
 * Initialise native push. Safe to call once on app start.
 */
export async function initNativePush() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    if (!listenersAdded) {
      listenersAdded = true;

      // Fired once FCM issues a token (also on token refresh).
      PushNotifications.addListener('registration', (token) => {
        cachedToken = token.value;
        postToken();
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('[push] Registration error:', JSON.stringify(err));
      });

      // User tapped a notification -> open the screen from its `url` data.
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const url = action?.notification?.data?.url;
        if (!url) return;
        try {
          const u = new URL(url);
          window.location.assign(u.pathname + u.search + u.hash);
        } catch {
          window.location.assign(url);
        }
      });
    }

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      // The user can still receive a token; Android only needs the
      // POST_NOTIFICATIONS permission to *display* notifications. Register anyway
      // so the token is stored, and notifications appear once they enable them.
      console.log('[push] Notification display permission not granted; registering for token anyway');
    }
    await PushNotifications.register();
  } catch (e) {
    console.error('[push] init error:', e?.message || e);
  }
}

/**
 * Re-send the cached token (e.g. right after the user logs in).
 */
export function syncPushToken() {
  if (!Capacitor.isNativePlatform()) return;
  postToken();
}

/**
 * Remove this device's token from the user (e.g. on logout).
 */
export async function unregisterPushToken() {
  if (!Capacitor.isNativePlatform() || !cachedToken) return;
  try {
    await axiosinstance.post('/fcm/unregister', { token: cachedToken });
  } catch (e) {
    console.error('[push] Failed to unregister FCM token:', e?.message || e);
  }
}
